import { useState } from "react";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Message } from "primereact/message";

import { EmptyState, PageHeader, PointsBadge, StatusBadge } from "../../components/ui";
import { useDebounce } from "../../hooks/useDebounce";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useTenantOptions } from "../../hooks/useTenantOptions";
import {
  AdminLedgerEntry,
  PointLedgerFilters as PointLedgerQueryFilters,
  formatPointLedgerPoints,
  formatPointLedgerRelated,
  formatPointLedgerRule,
  pointLedgerActionLabel,
  pointLedgerRelatedTypeLabel,
} from "../../models/pointLedger";
import { useGetPointLedgerQuery } from "../../store/services/point-ledger";
import { toReportDateParam } from "../../utils/constants/report-date-range";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import {
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "../Reporting/components/BenefitsMetricsWidgets";
import { PointsPageCard, PointsPageLoading } from "../PointRules/components/PointsPageCard";
import {
  DEFAULT_POINT_LEDGER_FILTERS,
  PointLedgerFilterValues,
  PointLedgerFilters,
  isPointLedgerFiltered,
} from "./components/PointLedgerFilters";
import { PointAdjustmentDialog } from "./components/PointAdjustmentDialog";

const PAGE_TITLE = "Movimientos de puntos";
const PAGE_HINT =
  "Por defecto se ven los descuentos. Una fila revertida ya devolvió los puntos: no corresponde reclamarlos.";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS = 25;
const SEARCH_DEBOUNCE_MS = 300;
const NO_VALUE = "—";

const toDayKeyOrNull = (date: Date | null): string | null => (date ? toReportDateParam(date) : null);

export const PointLedger = () => {
  const scope = useTenantScope();
  const { options: tenantOptions } = useTenantOptions();
  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  // SUPER_ADMIN en "Todos" ve los movimientos de todas las instituciones: ahí no aplica el flag
  // de un tenant.
  const isAllTenantsScope = !scope.loading && scope.role === "SUPER_ADMIN" && scope.tenantId === null;
  const flagReady = isAllTenantsScope || (!pointsFlag.loading && pointsFlag.enabled);
  const tenantId = scope.tenantId;

  const [filters, setFilters] = useState<PointLedgerFilterValues>(DEFAULT_POINT_LEDGER_FILTERS);
  const [adjustmentVisible, setAdjustmentVisible] = useState(false);
  // El único filtro que se tipea: sin debounce, pegar un mail de 25 caracteres son 25 requests.
  // Los demás son desplegables y calendarios, que cambian de una sola vez. Mismo tiempo que el
  // buscador de Asistencias.
  const debouncedEmail = useDebounce(filters.email, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [filtersTenantId, setFiltersTenantId] = useState(tenantId);

  // Cambiar el tenant del header vuelve a la primera página: la página 7 de otra institución
  // no tiene por qué existir acá.
  if (filtersTenantId !== tenantId) {
    setFiltersTenantId(tenantId);
    setPage(1);
  }

  /**
   * Los filtros tal como están APLICADOS, con el mail que ya soltó el debounce. El cartel de la
   * tabla vacía se explica con éstos y no con lo que se está tecleando: si no, mientras el
   * debounce corre diría que el vacío es del mail recién escrito, que todavía no se consultó.
   */
  const appliedFilters: PointLedgerFilterValues = { ...filters, email: debouncedEmail };

  const queryFilters: PointLedgerQueryFilters = {
    tenantId,
    sign: appliedFilters.sign,
    actionType: appliedFilters.actionType,
    // Sin recortar: el `trim()` y la decisión de mandarlo o no viven en `toPointLedgerParams`.
    email: appliedFilters.email,
    from: toDayKeyOrNull(appliedFilters.dateFrom),
    to: toDayKeyOrNull(appliedFilters.dateTo),
  };

  const {
    currentData: ledgerPage,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetPointLedgerQuery(
    { ...queryFilters, page, limit: rows },
    { skip: scope.loading || !flagReady }
  );

  const changeFilters = (next: PointLedgerFilterValues) => {
    setFilters(next);
    setPage(1);
  };

  const handlePage = (event: DataTablePageEvent) => {
    setRows(event.rows);
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  if (scope.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!isAllTenantsScope) {
    if (pointsFlag.loading) {
      return <PointsPageLoading title={PAGE_TITLE} />;
    }
    if (!pointsFlag.enabled) {
      return (
        <PointsPageCard>
          <PageHeader title={PAGE_TITLE} />
          <div className="flex justify-content-center p-4">
            <Message severity="info" text="La sección está deshabilitada para este tenant." />
          </div>
        </PointsPageCard>
      );
    }
  }

  const userBodyTemplate = (entry: AdminLedgerEntry) => (
    <div className="flex flex-column">
      <span>{entry.user.fullName || NO_VALUE}</span>
      {entry.user.email && <small className="text-600">{entry.user.email}</small>}
    </div>
  );

  // El signo va en el pill: un descuento se lee rojo y con "−" sin leer la fila entera.
  const pointsBodyTemplate = (entry: AdminLedgerEntry) => (
    <PointsBadge label={formatPointLedgerPoints(entry.points)} isPenalty={entry.points < 0} />
  );

  const relatedBodyTemplate = (entry: AdminLedgerEntry) => {
    const label = formatPointLedgerRelated(entry.related);
    if (label === null || entry.related === null) {
      return NO_VALUE;
    }
    return (
      <div className="flex flex-column">
        <span>{label}</span>
        <small className="text-600">{pointLedgerRelatedTypeLabel(entry.related.type)}</small>
      </div>
    );
  };

  /**
   * El motivo sólo existe en los movimientos que cargó una persona. Se muestran los DOS: el
   * del usuario arriba, porque es lo que la persona está leyendo en su app cuando llama a
   * preguntar, y el interno abajo, que es el que explica la decisión.
   */
  const reasonBodyTemplate = (entry: AdminLedgerEntry) => {
    if (entry.adjustment === null) return NO_VALUE;
    return (
      <div className="flex flex-column">
        <span>{entry.adjustment.userReason}</span>
        <small className="text-600">
          {entry.adjustment.internalReason} · {entry.adjustment.createdBy.fullName}
        </small>
      </div>
    );
  };

  const statusBodyTemplate = (entry: AdminLedgerEntry) =>
    entry.isReversed ? (
      <StatusBadge label="Revertida" tone="warning" />
    ) : (
      <StatusBadge label="Vigente" tone="success" />
    );

  return (
    <PointsPageCard>
      <BenefitsMetricsTenantScopeHeader tenantId={tenantId} />
      {/*
        El alta va en el `+` del encabezado y no en un botón suelto sobre la tabla: §6.1 del
        SYSTEM_DESIGN manda un ícono circular contiguo al título, que es como se crea en
        Reglas, Premios y el resto del Backoffice.
      */}
      <PageHeader
        title={PAGE_TITLE}
        onCreate={() => setAdjustmentVisible(true)}
        createLabel="Nuevo movimiento de puntos"
      />
      <p className="text-center text-color-secondary mb-4 mt-0">{PAGE_HINT}</p>

      <PointAdjustmentDialog
        visible={adjustmentVisible}
        onHide={() => setAdjustmentVisible(false)}
        tenantId={tenantId}
        tenantOptions={tenantOptions}
      />

      <PointLedgerFilters filters={filters} onChange={changeFilters} />
      <BenefitsMetricsRefreshingIndicator visible={isFetching && !isLoading} />

      {isError ? (
        <Message
          severity="error"
          text={getApiErrorMessage(error) ?? "No se pudieron cargar los movimientos."}
        />
      ) : (
        <DataTable
          value={ledgerPage?.items ?? []}
          loading={isLoading}
          dataKey="id"
          lazy
          paginator
          rows={rows}
          first={(page - 1) * rows}
          totalRecords={ledgerPage?.total ?? 0}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPage={handlePage}
          stripedRows
          responsiveLayout="scroll"
          // Una fila revertida queda grisada además del badge: el admin no puede confundirla
          // con un descuento vigente de un vistazo.
          rowClassName={(entry: AdminLedgerEntry) => (entry.isReversed ? "surface-200" : "")}
          emptyMessage={
            <EmptyState entity="movimientos" filtered={isPointLedgerFiltered(appliedFilters)} />
          }
        >
          {/*
            En scope "Todos" sin esta columna la grilla no es atribuible: dos homónimos de
            instituciones distintas se leen igual, y un reclamo de "me descontaron puntos" no
            se puede ubicar. Es el mismo criterio que la pantalla de canjes.
          */}
          {isAllTenantsScope && (
            <Column
              header="Institución"
              body={(entry: AdminLedgerEntry) =>
                tenantOptions.find((option) => option.value === entry.tenantId)?.label ??
                entry.tenantId
              }
            />
          )}
          <Column header="Persona" body={userBodyTemplate} />
          <Column header="Puntos" body={pointsBodyTemplate} />
          <Column
            header="Acción"
            body={(entry: AdminLedgerEntry) => pointLedgerActionLabel(entry.actionType)}
          />
          <Column
            header="Regla"
            body={(entry: AdminLedgerEntry) => formatPointLedgerRule(entry.rule) ?? NO_VALUE}
          />
          <Column header="Evento o premio" body={relatedBodyTemplate} />
          <Column header="Motivo" body={reasonBodyTemplate} />
          <Column
            header="Fecha"
            body={(entry: AdminLedgerEntry) => formatDateWithoutSeconds(entry.createdAt)}
          />
          <Column header="Estado" body={statusBodyTemplate} />
        </DataTable>
      )}
    </PointsPageCard>
  );
};
